import React, { useEffect } from 'react';
import { Outlet, createRootRouteWithContext } from '@tanstack/react-router';
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools';
import { toast } from 'sonner';
import type { Auth0ContextType } from '../auth/auth0';
import { NotFound } from '@/client/components/404';
import { ScaledroneProvider } from '@/client/providers/ScaledroneProvider';
import { SwapClientDialog } from '@/client/components/swap-client-dialog';
import { useCCSwapStore } from '@/client/store/ccSwapStore';

interface RouterContext {
  auth: Auth0ContextType;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
  notFoundComponent: NotFound,
});

function RootComponent() {
  const ccSwap = useCCSwapStore();

  // After a client swap + Auth0 redirect, show a toast with a link to the target URL.
  // We can't auto-open via window.open() because browsers block non-user-initiated popups.
  useEffect(() => {
    const openAfterSwap = sessionStorage.getItem('openAfterSwap');
    if (openAfterSwap) {
      sessionStorage.removeItem('openAfterSwap');
      toast.success('Client switched successfully', {
        description: 'Click to open Control Center',
        duration: 10000,
        action: {
          label: 'Open',
          onClick: () => window.open(openAfterSwap, '_blank', 'noopener,noreferrer'),
        },
      });
    }
  }, []);

  return (
    <ScaledroneProvider>
      <div>
        <Outlet />
      </div>
      {process.env.NODE_ENV === 'development' && <TanStackRouterDevtools position="bottom-right"/>}

      {/* Global swap dialog for CC links — lives here so WSS table re-renders don't kill it */}
      <SwapClientDialog
        open={ccSwap.open}
        onOpenChange={(open) => { if (!open) ccSwap.close(); }}
        targetClientID={ccSwap.targetClientID}
        targetClientName={ccSwap.targetClientName}
        openUrlAfterSwap={ccSwap.ccUrl}
      />
    </ScaledroneProvider>
  );
}
